import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import moment from "moment";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import "../news/news.scss";

const News = () => {
    const location = useLocation();

    const postsPage = location.pathname.split("/")[2] || "blasorchester";
    const postsCap = postsPage.charAt(0).toUpperCase() + postsPage.slice(1);

    const [posts, setPosts] = useState([]);
    const [isLoading, setLoading] = useState(true);

    // Filter
    const [selectedGrad, setSelectedGrad] = useState("");
    const [selectedDauer, setSelectedDauer] = useState("");
    const [selectedJahr, setSelectedJahr] = useState("");
    const [selectedKategorie, setSelectedKategorie] = useState("");

    useEffect(() => {
        const fetchAllPosts = async () => {
            setLoading(true);

            try {
                const res = await axios.get(
                    "https://api.augschoell.com/wp-json/wp/v2/kompositionen?per_page=100"
                );

                const filteredData = res.data.filter(
                    (post) => post.acf?.type === postsCap
                );

                setPosts(filteredData);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAllPosts();
    }, [postsCap]);

    // Filteroptionen dynamisch aus den Kompositionen erstellen
    const gradOptionen = useMemo(
        () =>
            [...new Set(
                posts
                    .map((post) => post.acf?.grad)
                    .filter(Boolean)
            )].sort((a, b) =>
                String(a).localeCompare(String(b), "de", {
                    numeric: true,
                    sensitivity: "base",
                })
            ),
        [posts]
    );

    const dauerOptionen = [
        { label: "0–3 Minuten", value: "0-3" },
        { label: "3–5 Minuten", value: "3-5" },
        { label: "5–10 Minuten", value: "5-10" },
        { label: "10+ Minuten", value: "10+" },
    ];

    const jahrOptionen = useMemo(
        () =>
            [...new Set(
                posts
                    .map((post) => {
                        const jahr = post.acf?.year;

                        if (!jahr) return null;

                        const parsedYear = moment(jahr).year();

                        return Number.isNaN(parsedYear) ? null : parsedYear;
                    })
                    .filter(Boolean)
            )].sort((a, b) => b - a),
        [posts]
    );

    const kategorieOptionen = useMemo(
        () =>
            [...new Set(
                posts
                    .map((post) => post.acf?.category)
                    .filter(Boolean)
            )].sort((a, b) =>
                String(a).localeCompare(String(b), "de", {
                    sensitivity: "base",
                })
            ),
        [posts]
    );

    // Kompositionen anhand der ausgewählten Filter filtern
    const filteredPosts = useMemo(() => {
        return posts.filter((post) => {
            const grad = post.acf?.grad ?? "";
            const kategorie = post.acf?.category ?? "";
            const dauer = parseFloat(
                String(post.acf?.dur ?? "").replace(",", ".")
            );

            const jahr = post.acf?.year
                ? moment(post.acf.year).year()
                : null;

            const dauerPasst =
                !selectedDauer ||
                (selectedDauer === "0-3" && dauer >= 0 && dauer < 3) ||
                (selectedDauer === "3-5" && dauer >= 3 && dauer < 5) ||
                (selectedDauer === "5-10" && dauer >= 5 && dauer < 10) ||
                (selectedDauer === "10+" && dauer >= 10);

            return (
                (!selectedGrad || grad === selectedGrad) &&
                dauerPasst &&
                (!selectedJahr || jahr === Number(selectedJahr)) &&
                (!selectedKategorie || kategorie === selectedKategorie)
            );
        });
    }, [
        posts,
        selectedGrad,
        selectedDauer,
        selectedJahr,
        selectedKategorie,
    ]);

    const resetFilters = () => {
        setSelectedGrad("");
        setSelectedDauer("");
        setSelectedJahr("");
        setSelectedKategorie("");
    };

    const hasActiveFilters =
        selectedGrad ||
        selectedDauer ||
        selectedJahr ||
        selectedKategorie;

    return (
        <div className="news">
            <Helmet>
                <title>{postsCap} | Jakob Augschöll</title>
            </Helmet>

            <motion.div
                className="select"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
            >
                <Link
                    to="/posts/blasorchester"
                    className={postsPage === "blasorchester" ? "selected" : ""}
                >
                    Blasorchester
                </Link>

                <Link
                    to="/posts/ensemble"
                    className={postsPage === "ensemble" ? "selected" : ""}
                >
                    Ensemble
                </Link>

                <Link
                    to="/posts/arrangement"
                    className={postsPage === "arrangement" ? "selected" : ""}
                >
                    Arrangements
                </Link>
            </motion.div>

            <motion.div
                className="filters"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
            >
                <div className="filter">
                    <label htmlFor="filter-grad">Grad</label>
                    <select
                        id="filter-grad"
                        value={selectedGrad}
                        onChange={(e) => setSelectedGrad(e.target.value)}
                    >
                        <option value="">Alle Grade</option>
                        {gradOptionen.map((grad) => (
                            <option key={grad} value={grad}>
                                {grad}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter">
                    <label htmlFor="filter-dauer">Dauer</label>
                    <select
                        id="filter-dauer"
                        value={selectedDauer}
                        onChange={(e) => setSelectedDauer(e.target.value)}
                    >
                        <option value="">Alle Dauern</option>

                        {dauerOptionen.map((dauer) => (
                            <option key={dauer.value} value={dauer.value}>
                                {dauer.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter">
                    <label htmlFor="filter-jahr">Jahr</label>
                    <select
                        id="filter-jahr"
                        value={selectedJahr}
                        onChange={(e) => setSelectedJahr(e.target.value)}
                    >
                        <option value="">Alle Jahre</option>
                        {jahrOptionen.map((jahr) => (
                            <option key={jahr} value={jahr}>
                                {jahr}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter">
                    <label htmlFor="filter-kategorie">Kategorie</label>
                    <select
                        id="filter-kategorie"
                        value={selectedKategorie}
                        onChange={(e) => setSelectedKategorie(e.target.value)}
                    >
                        <option value="">Alle Kategorien</option>
                        {kategorieOptionen.map((kategorie) => (
                            <option key={kategorie} value={kategorie}>
                                {kategorie}
                            </option>
                        ))}
                    </select>
                </div>

                {hasActiveFilters && (
                    <button
                        type="button"
                        className="resetFilters"
                        onClick={resetFilters}
                    >
                        Filter zurücksetzen
                    </button>
                )}
            </motion.div>

            <div className="resultCount">
                {!isLoading &&
                    `${filteredPosts.length} ${filteredPosts.length === 1
                        ? "Komposition"
                        : "Kompositionen"
                    }`}
            </div>

            <div className="container">
                {isLoading ? (
                    <div className="loading" />
                ) : filteredPosts.length > 0 ? (
                    filteredPosts.map((post, index) => {
                        const delay = (index % 5) * 0.1;

                        return (
                            <motion.div
                                key={post.id}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.5,
                                    ease: "easeOut",
                                    delay,
                                }}
                            >
                                <Link
                                    className="grid"
                                    to={`/post/${post.slug}`}
                                >
                                    <div className="inline">
                                        <span className="postTyp">
                                            {post.acf.type}
                                        </span>

                                        <span className="postKategorie">
                                            {post.acf.category}
                                        </span>

                                        <h3>{post.title.rendered}</h3>

                                        <span className="postDatum">
                                            {post.acf.year
                                                ? moment(post.acf.year).format("YYYY")
                                                : ""}
                                        </span>

                                        <span className="postBg">
                                            {post.acf.type?.[0]}
                                        </span>
                                    </div>

                                    <div className="content">
                                        <div className="raster">
                                            {post.acf.audio ? (
                                                <span className="element">
                                                    <PlayArrowOutlinedIcon />
                                                </span>
                                            ) : (
                                                <span className="element empty">
                                                    <PlayArrowOutlinedIcon />
                                                </span>
                                            )}

                                            {post.acf.pdf ? (
                                                <span className="element">
                                                    PDF
                                                </span>
                                            ) : (
                                                <span className="element empty">
                                                    PDF
                                                </span>
                                            )}

                                            {post.acf.dur && (
                                                <span className="element">
                                                    {post.acf.dur}
                                                </span>
                                            )}

                                            {post.acf.grad && (
                                                <span className="element">
                                                    {post.acf.grad}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })
                ) : (
                    <div className="noResults">
                        <p>Keine Werke gefunden.</p>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                className="resetFilters"
                                onClick={resetFilters}
                            >
                                Filter zurücksetzen
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default News;